import React, { useState } from 'react';
import { CheckIcon, StarIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { TrophyIcon as TrophyIconSolid } from '@heroicons/react/24/solid';

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  billing: 'monthly' | 'yearly';
  description: string;
  features: string[];
  popular?: boolean;
  current?: boolean;
}

interface PayPalSubscriptionData {
  subscriptionID: string;
  status: 'APPROVAL_PENDING' | 'APPROVED' | 'ACTIVE' | 'SUSPENDED' | 'CANCELLED';
  planId: string;
}

const PremiumSubscription: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<string>('premium-yearly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentSubscription] = useState<PayPalSubscriptionData | null>(null);

  const plans: PricingPlan[] = [
    {
      id: 'basic',
      name: 'Basic',
      price: 0,
      billing: 'monthly',
      description: 'Perfect for getting started',
      features: [
        'Basic business listing',
        'Contact information display',
        'Map location pin',
        'Basic product listings (up to 5)',
        'Standard search visibility'
      ]
    },
    {
      id: 'premium-monthly',
      name: 'Premium',
      price: 9.99,
      billing: 'monthly',
      description: 'Enhanced features for growing businesses',
      features: [
        'Everything in Basic',
        'Enhanced business profile',
        'Unlimited product listings',
        'Featured listing in search results',
        'Priority customer support',
        'Advanced analytics dashboard',
        'Social media integration',
        'Custom business hours',
        'Photo gallery (up to 20 images)',
        'Customer review management'
      ]
    },
    {
      id: 'premium-yearly',
      name: 'Premium Annual',
      price: 89,
      billing: 'yearly',
      description: 'Best value with 2 months free!',
      popular: true,
      features: [
        'Everything in Premium Monthly',
        '🎯 2 months FREE (worth $19.98)',
        'Premium badge display',
        'Advanced SEO optimization',
        'Email marketing integration',
        'Inventory management tools',
        'Bulk product import/export',
        'Priority placement in directory',
        'Dedicated account manager'
      ]
    }
  ];

  const handlePlanSelection = (planId: string) => {
    setSelectedPlan(planId);
  };

  const handleSubscribe = async () => {
    setIsProcessing(true);
    
    try {
      // Initialize PayPal subscription
      const response = await fetch('/api/subscriptions/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: selectedPlan,
          returnUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription/cancelled`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }

      const data = await response.json();
      
      // Redirect to PayPal for approval
      window.location.href = data.approvalUrl;
      
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to process subscription. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentSubscription) return;
    
    if (!confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      return;
    }

    try {
      const response = await fetch(`/api/subscriptions/${currentSubscription.subscriptionID}/cancel`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      alert('Subscription cancelled successfully. You will retain access until the end of your billing period.');
      // Refresh subscription status
      window.location.reload();
      
    } catch (error) {
      console.error('Cancellation error:', error);
      alert('Failed to cancel subscription. Please contact support.');
    }
  };

  const getPlanCard = (plan: PricingPlan) => {
    const isSelected = selectedPlan === plan.id;
    const isCurrent = plan.id === currentSubscription?.planId;
    
    return (
      <div
        key={plan.id}
        className={`relative border-2 rounded-lg p-6 cursor-pointer transition-all ${
          plan.popular
            ? 'border-amber-500 bg-amber-50'
            : isSelected
            ? 'border-amber-400 bg-amber-25'
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => handlePlanSelection(plan.id)}
      >
        {plan.popular && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-medium">
              Most Popular
            </span>
          </div>
        )}

        {isCurrent && (
          <div className="absolute -top-3 right-4">
            <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
              Current Plan
            </span>
          </div>
        )}

        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-2">
            <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
            {plan.id !== 'basic' && (
              <TrophyIconSolid className="w-5 h-5 text-amber-500 ml-2" />
            )}
          </div>
          
          <p className="text-gray-600 mb-4">{plan.description}</p>
          
          <div className="mb-4">
            <span className="text-4xl font-bold text-gray-900">
              ${plan.price}
            </span>
            <span className="text-gray-600 ml-1">
              /{plan.billing === 'yearly' ? 'year' : 'month'}
            </span>
          </div>

          {plan.billing === 'yearly' && plan.price > 0 && (
            <p className="text-sm text-green-600 font-medium">
              Save ${((9.99 * 12) - plan.price).toFixed(2)} per year!
            </p>
          )}
        </div>

        <ul className="space-y-3 mb-6">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <CheckIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>

        {!isCurrent && (
          <button
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              plan.id === 'basic'
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : isSelected
                ? 'bg-amber-600 text-white hover:bg-amber-700'
                : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
            }`}
            disabled={plan.id === 'basic' || isProcessing}
          >
            {plan.id === 'basic' ? 'Current Plan' : 'Select Plan'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <TrophyIcon className="w-8 h-8 text-amber-500 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Premium Membership</h1>
          </div>
          <p className="text-xl text-gray-600 mb-6">
            Unlock advanced features and grow your beekeeping business
          </p>
          <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
            <div className="flex items-center">
              <StarIcon className="w-4 h-4 text-amber-400 mr-1" />
              <span>Priority Support</span>
            </div>
            <div className="flex items-center">
              <CheckIcon className="w-4 h-4 text-green-500 mr-1" />
              <span>30-Day Money Back</span>
            </div>
            <div className="flex items-center">
              <TrophyIcon className="w-4 h-4 text-amber-500 mr-1" />
              <span>Exclusive Features</span>
            </div>
          </div>
        </div>

        {/* Current Subscription Status */}
        {currentSubscription && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  Active Premium Subscription
                </h3>
                <p className="text-green-700">
                  Status: <span className="font-medium">{currentSubscription.status}</span>
                </p>
                <p className="text-green-700">
                  Subscription ID: <span className="font-mono text-sm">{currentSubscription.subscriptionID}</span>
                </p>
              </div>
              <button
                onClick={handleCancelSubscription}
                className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors"
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        )}

        {/* Pricing Plans */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {plans.map(getPlanCard)}
        </div>

        {/* Subscribe Button */}
        {selectedPlan !== 'basic' && !currentSubscription && (
          <div className="text-center">
            <button
              onClick={handleSubscribe}
              disabled={isProcessing}
              className="bg-amber-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Processing...
                </div>
              ) : (
                `Subscribe to ${plans.find(p => p.id === selectedPlan)?.name} - $${plans.find(p => p.id === selectedPlan)?.price}/${plans.find(p => p.id === selectedPlan)?.billing === 'yearly' ? 'year' : 'month'}`
              )}
            </button>
            
            <p className="text-sm text-gray-600 mt-4">
              Secure payment processing through PayPal. Cancel anytime.
            </p>
          </div>
        )}

        {/* Features Comparison */}
        <div className="bg-white rounded-lg shadow-lg p-8 mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Feature Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4">Feature</th>
                  <th className="text-center py-3 px-4">Basic</th>
                  <th className="text-center py-3 px-4">Premium</th>
                </tr>
              </thead>
              <tbody className="space-y-2">
                {[
                  { feature: 'Business Listing', basic: true, premium: true },
                  { feature: 'Map Location', basic: true, premium: true },
                  { feature: 'Product Listings', basic: '5 max', premium: 'Unlimited' },
                  { feature: 'Search Visibility', basic: 'Standard', premium: 'Featured' },
                  { feature: 'Photo Gallery', basic: '3 images', premium: '20 images' },
                  { feature: 'Analytics Dashboard', basic: false, premium: true },
                  { feature: 'Social Media Integration', basic: false, premium: true },
                  { feature: 'Priority Support', basic: false, premium: true },
                  { feature: 'Premium Badge', basic: false, premium: true },
                  { feature: 'SEO Optimization', basic: false, premium: true }
                ].map((row, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-medium text-gray-900">{row.feature}</td>
                    <td className="py-3 px-4 text-center">
                      {typeof row.basic === 'boolean' ? (
                        row.basic ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )
                      ) : (
                        <span className="text-gray-700">{row.basic}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {typeof row.premium === 'boolean' ? (
                        row.premium ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )
                      ) : (
                        <span className="text-gray-700">{row.premium}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                question: "Can I cancel my subscription anytime?",
                answer: "Yes, you can cancel your subscription at any time. You'll retain access to premium features until the end of your current billing period."
              },
              {
                question: "Is there a free trial?",
                answer: "We offer a 30-day money-back guarantee. If you're not satisfied with Premium within 30 days, we'll refund your payment."
              },
              {
                question: "What payment methods do you accept?",
                answer: "We accept all major credit cards, debit cards, and PayPal through our secure PayPal payment system."
              },
              {
                question: "Can I upgrade or downgrade my plan?",
                answer: "Yes, you can change your plan at any time. Upgrades take effect immediately, and downgrades take effect at the next billing cycle."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumSubscription;